import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Megaphone,
  Plus,
  Trash2,
  Edit,
  Calendar,
  User,
  X,
  AlertCircle,
  Users,
  Search,
  Filter,
} from "lucide-react";
import { toast } from "react-toastify";

const PriorityBadge = ({ priority }) => {
  const styles = {
    High: "bg-red-500/20 text-red-400 border-red-500/50",
    Medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
    Low: "bg-blue-500/20 text-blue-400 border-blue-500/50",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold border ${
        styles[priority] || styles.Low
      }`}
    >
      {priority} Priority
    </span>
  );
};

const AnnouncementsPage = () => {
  // Mock Data
  const [announcements, setAnnouncements] = useState([
    {
      id: 1,
      title: "Diwali Holidays",
      content:
        "School will remain closed from 1st Nov to 5th Nov for Diwali celebrations. Wishing everyone a happy and safe Diwali!",
      date: "2023-10-25",
      author: "Admin",
      priority: "High",
      audience: "All",
    },
    {
      id: 2,
      title: "New Batch Registration",
      content:
        "Registration for the new JEE Batch starts next week. Interested students can collect forms from the office.",
      date: "2023-10-20",
      author: "Admin",
      priority: "Medium",
      audience: "Students",
    },
    {
      id: 3,
      title: "Staff Meeting",
      content:
        "Monthly staff meeting will be held on Saturday at 2 PM in the conference room. Attendance is mandatory.",
      date: "2023-10-18",
      author: "Principal",
      priority: "High",
      audience: "Teachers",
    },
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPriority, setFilterPriority] = useState("All");

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    priority: "Medium",
    audience: "All",
    date: new Date().toISOString().split("T")[0],
  });

  // Handlers
  const handleSave = (e) => {
    e.preventDefault();
    if (editingItem) {
      setAnnouncements(
        announcements.map((a) =>
          a.id === editingItem.id
            ? { ...formData, id: a.id, author: "Admin" }
            : a
        )
      );
      toast.success("Announcement updated successfully");
    } else {
      setAnnouncements([
        { ...formData, id: Date.now(), author: "Admin" },
        ...announcements,
      ]);
      toast.success("Announcement created successfully");
    }
    handleCloseModal();
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      setAnnouncements(announcements.filter((a) => a.id !== id));
      toast.success("Announcement deleted successfully");
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({
      title: "",
      content: "",
      priority: "Medium",
      audience: "All",
      date: new Date().toISOString().split("T")[0],
    });
  };

  // Filter Logic
  const filteredAnnouncements = announcements.filter((a) => {
    const matchesSearch =
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterPriority === "All" || a.priority === filterPriority;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-yellow-600 to-orange-600 rounded-2xl shadow-lg shadow-yellow-500/20">
                <Megaphone className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white">Announcements</h1>
            </div>
            <p className="text-gray-400 mt-1 ml-1">
              Broadcast important news to students and teachers
            </p>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-yellow-600 to-orange-600 text-white rounded-xl hover:shadow-lg hover:shadow-yellow-500/20 transition-all font-semibold"
          >
            <Plus className="w-5 h-5" />
            Create Announcement
          </motion.button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 bg-gray-800/30 p-4 rounded-2xl border border-gray-700/30 backdrop-blur-sm">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-900/50 border border-gray-700 rounded-xl py-2.5 pl-10 pr-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-yellow-500/50 transition-colors"
            />
          </div>
          <div className="flex gap-2">
            {["All", "High", "Medium", "Low"].map((priority) => (
              <button
                key={priority}
                onClick={() => setFilterPriority(priority)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterPriority === priority
                    ? "bg-gray-700 text-white"
                    : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
                }`}
              >
                {priority}
              </button>
            ))}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredAnnouncements.map((announcement) => (
              <motion.div
                key={announcement.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-gray-800/40 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-yellow-500/30 transition-all group hover:bg-gray-800/60 flex flex-col h-full"
              >
                <div className="flex justify-between items-start mb-4">
                  <PriorityBadge priority={announcement.priority} />
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditModal(announcement)}
                      className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                  {announcement.title}
                </h3>
                <p className="text-gray-400 text-sm mb-6 line-clamp-4 flex-grow">
                  {announcement.content}
                </p>

                <div className="pt-4 border-t border-gray-700/50 flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-4">
                    <div
                      className="flex items-center gap-1.5"
                      title="Target Audience"
                    >
                      <Users className="w-3.5 h-3.5 text-gray-400" />
                      {announcement.audience}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      {announcement.date}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-gray-400" />
                    {announcement.author}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filteredAnnouncements.length === 0 && (
          <div className="text-center py-20">
            <div className="inline-flex p-4 bg-gray-800/50 rounded-full mb-4">
              <Megaphone className="w-8 h-8 text-gray-600" />
            </div>
            <h3 className="text-xl font-medium text-white mb-2">
              No announcements found
            </h3>
            <p className="text-gray-400">
              Create a new announcement to get started
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
            >
              <div className="p-6 border-b border-gray-800 flex justify-between items-center bg-gray-800/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/10 rounded-lg">
                    <Megaphone className="w-5 h-5 text-yellow-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    {editingItem ? "Edit Announcement" : "New Announcement"}
                  </h2>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Title
                  </label>
                  <input
                    required
                    type="text"
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white focus:border-yellow-500 focus:outline-none transition-colors"
                    placeholder="e.g., Exam Schedule Release"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Priority
                    </label>
                    <select
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white focus:border-yellow-500 focus:outline-none transition-colors appearance-none"
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: e.target.value })
                      }
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Target Audience
                    </label>
                    <select
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white focus:border-yellow-500 focus:outline-none transition-colors appearance-none"
                      value={formData.audience}
                      onChange={(e) =>
                        setFormData({ ...formData, audience: e.target.value })
                      }
                    >
                      <option value="All">All</option>
                      <option value="Students">Students</option>
                      <option value="Teachers">Teachers</option>
                      <option value="Parents">Parents</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Content
                  </label>
                  <textarea
                    required
                    rows="4"
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-yellow-500 focus:outline-none transition-colors resize-none"
                    placeholder="Write the announcement details here..."
                    value={formData.content}
                    onChange={(e) =>
                      setFormData({ ...formData, content: e.target.value })
                    }
                  ></textarea>
                </div>

                <div className="flex gap-3 mt-6 pt-2">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors font-medium border border-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-xl hover:shadow-lg hover:shadow-yellow-500/20 transition-all font-medium"
                  >
                    {editingItem ? "Update" : "Publish"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnnouncementsPage;
